import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { createCtx } from '@shared/util/context';

/**
 * This module provides a simple navigation stack via a provider & context or just a hook.
 * The nav stack can be initialised with an array of routes, or it will default to an empty array.
 * Use the functions to modify the stack and currentRoute to inspect the value. The hook
 * will trigger a re-render when the stack is modified.
 *
 * There are two modes of usage:
 *
 * 1) Use within a single component:
 *
 *     const nav = useNavStack([{name:'list'}]);
 *     const onClick = () => nav.push({name:'details'});
 *     const onBack = () => nav.pop();
 *     return nav.currentRoute.name === 'list' ? <List/> : <Details/>;
 *
 * 2) Use in a component tree:
 *
 *     <MBNavStackProvider initialRoutes={[{name:'list'}]}>{component}</MBNavStackProvider>
 *     ...
 *     const nav = useNavStackContext<YourRouteType>();
 *     ...
 */

export interface Route<RouteName = string> {
  name: RouteName;
  params?: { [key: string]: any };
}

export interface NavStack<RouteName = string> {
  pop(): Route<RouteName> | undefined;
  push(route: Route<RouteName>): void;
  replace(route: Route<RouteName>): void;
  reset(routes?: Route<RouteName>[]): void;
  currentRoute: Route<RouteName> | undefined;
}

const [, NavStackContextProvider, NavStackContext] = createCtx<NavStack>();

type ExtractRouteName<TRoute> = TRoute extends { name: infer RouteName } ? RouteName : string;

/**
 * Hook to access the navigation stack from context.
 * Requires a type parameter for type safety:
 *
 * - useNavStackContext<TRoute>() - returns typed NavStack with typed currentRoute
 */
export function useNavStackContext<TRoute extends Route<string>>(): NavStack<
  ExtractRouteName<TRoute>
> & {
  currentRoute: TRoute | undefined;
} {
  const ctx = useContext(NavStackContext);
  if (!ctx) {
    throw new Error('useNavStackContext must be used within a MBNavStackProvider');
  }
  return ctx as NavStack<ExtractRouteName<TRoute>> & { currentRoute: TRoute | undefined };
}

/**
 * Hook to create a navigation stack.
 * @param initialRoutes - The initial routes to set in the stack.
 * @returns A NavStack object with the following methods:
 * - pop(): Removes the last route from the stack and returns it.
 * - push(route: Route): Adds a new route to the stack.
 * - replace(route: Route): Replaces the last route in the stack with a new one.
 * - reset(routes: Route[]): Resets the stack to a new set of routes.
 * - currentRoute: The current route at the top of the stack.
 */
export function useNavStack<RouteName = string>(
  initialRoutes: Route<RouteName>[] = [],
): NavStack<RouteName> {
  const [stack, setStack] = useState<Route<RouteName>[]>(initialRoutes);

  const pop = useCallback((): Route<RouteName> | undefined => {
    if (stack.length === 0) {
      return undefined;
    }
    let stackCopy = [...stack];
    const route = stackCopy.pop();
    setStack(stackCopy);
    return route;
  }, [stack]);

  const push = useCallback(
    (route: Route<RouteName>) => {
      let stackCopy = [...stack];
      stackCopy.push(route);
      setStack(stackCopy);
    },
    [stack],
  );

  const replace = useCallback(
    (route: Route<RouteName>) => {
      if (stack.length === 0) {
        return;
      }
      let stackCopy = [...stack];
      stackCopy[stackCopy.length - 1] = route;
      setStack(stackCopy);
    },
    [stack],
  );

  const reset = useCallback((routes: Route<RouteName>[] = []) => {
    setStack([...routes]);
  }, []);

  return useMemo<NavStack<RouteName>>(
    () => ({ pop, push, replace, reset, currentRoute: stack[stack.length - 1] }),
    [pop, push, replace, reset, stack],
  );
}

interface Props<TRoute extends Route<string>> {
  initialRoutes?: TRoute[];
  children: ReactNode;
}

export function MBNavStackProvider<TRoute extends Route<string>>({
  initialRoutes,
  children,
}: Props<TRoute>) {
  type RouteName = ExtractRouteName<TRoute>;
  const ctx = useNavStack<RouteName>(initialRoutes as unknown as Route<RouteName>[] | undefined);

  return <NavStackContextProvider value={ctx}>{children}</NavStackContextProvider>;
}
